import { useState, useEffect, useContext } from "react";
import { AnexosContext } from "../../../contexts/anexosProvider";
import { ClientContext } from "../../../contexts/clientProvider";
import { UserContext } from "../../../contexts/userProvider";
import { PatrimonioContext } from "../../../contexts/patrimonioProvider";
import { BiX } from "react-icons/bi";
import tipoMovimentacao from "./tipoMovimentacao";

const Movimenta = ({ setMovimentaModal, selectedPatrimonios, setNewPatrimonios, newPatrimonios, setSelectedPatrimonios, patrimonioType }) => {
  const { clientes } = useContext(ClientContext);
  const { anexosByClient } = useContext(AnexosContext);
  const { user, setAlert } = useContext(UserContext);
  const { movimentarPatrimonios } = useContext(PatrimonioContext);
  const [patrimonios, setPatrimonios] = useState([]);
  const [clients, setClients] = useState(null);
  const [anexos, setAnexos] = useState(null);
  const [movimentaData, setMovimentaData] = useState({});

  useEffect(() => {
    let patIds = selectedPatrimonios ? [...selectedPatrimonios] : [];
    setPatrimonios(patIds);
  }, [selectedPatrimonios]);

  useEffect(() => {
    let newClients = clientes ? clientes : [];
    setClients(newClients);
  }, [clientes]);

  async function handleSelectClient(e) {
    setAnexos(null);
    const idCliente = e.target.value;
    const anexos = await anexosByClient(idCliente);
    setAnexos(anexos);
    setMovimentaData((atual) => ({ ...atual, id_cliente: parseInt(idCliente) }));
  }

  // Nº do chamado do VoxDESK e motivo (opcionais): o chamado aparece para o cliente no
  // "o que mudou" do faturamento; o motivo fica só no histórico interno.
  function handleCampoTexto(campo) {
    return (e) => setMovimentaData((atual) => ({ ...atual, [campo]: e.target.value }));
  }

  async function handleSelectAnexo(e) {
    const idAnexo = e.target.value;
    setMovimentaData((atual) => ({ ...atual, id_anexo: parseInt(idAnexo) }));
  }

  const handleCloseModal = () => {
    setMovimentaModal(false);
  };

  const handleMovimenta = async () => {
    if(patrimonios.length === 0) return setAlert({ visible: true, title: "Atenção!", placeholder: "Selecione ao menos um patrimônio para movimentar", confirm: false, type: "warning" });
    let newMovimentaData = { ...movimentaData, usuario: user.nome, id_usuario: user.id, patrimonios: patrimonios };
    newMovimentaData.created_at = newMovimentaData.updated_at = new Date().toISOString().slice(0, 19).replace('T', ' ');

    newMovimentaData.tipo = tipoMovimentacao(patrimonioType);
    await movimentarPatrimonios(newMovimentaData);
    let patrimonioIds = patrimonios.map(pat => pat.id);

    let patrimoniosN = newPatrimonios.filter((pat) => {
      if (patrimonioIds.includes(pat.id)) {
        return false;
      }
      return true;
    });

    setNewPatrimonios(patrimoniosN);
    setSelectedPatrimonios([]);
    handleCloseModal();
  };


  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4' onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
      <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-vox-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col'>
        {/* Header */}
        <div className='bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-6 relative'>
          <button 
            onClick={handleCloseModal} 
            className='absolute top-4 right-4 p-2 text-white hover:text-red-200 transition-colors duration-200 rounded-full hover:bg-white/20'
          >
            <BiX className='w-8 h-8' />
          </button>
          
          <div className='pr-16'>
            <h1 className='text-3xl font-bold text-white mb-2'>
              Movimentar Patrimônios
            </h1>
            <div className='bg-white/20 rounded-lg px-4 py-2 inline-block'>
              <span className='text-xl font-bold text-white'>{patrimonios.length}</span>
              <span className='text-white/80 ml-2'>patrimônios selecionados</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='space-y-6'>
            {/* Cliente Selection */}
            <div className='space-y-2' data-tour-id='movimentar:cliente'>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                Cliente de Destino
              </label>
              <select 
                className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200"
                onChange={(e) => handleSelectClient(e)}
              >
                <option value="">Selecione o cliente</option>
                {clients &&
                  clients
                    .slice()
                    .sort((a, b) => a?.nome?.localeCompare(b?.nome))
                    .map((client, index) => (
                      <option key={index} value={client.id}>
                        {client.nome}
                      </option>
                    ))}
              </select>
            </div>

            {/* Anexo Selection */}
            <div className='space-y-2' data-tour-id='movimentar:anexo'>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                Anexo de Destino
              </label>
              <select 
                className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onChange={(e) => handleSelectAnexo(e)} 
                disabled={!anexos}
              >
                <option value="">Selecione o anexo</option>
                {anexos &&
                  anexos
                    .slice()
                    .sort((a, b) => a?.nome?.localeCompare(b?.nome))
                    .map((anexo, index) => (
                      <option key={index} value={anexo.id}>
                        {anexo.nome}
                      </option>
                    ))}
              </select>
            </div>

            {/* Chamado e motivo (opcionais) */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2' data-tour-id='movimentar:chamado'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Nº do chamado (VoxDESK) <span className='font-normal text-gray-400'>— opcional</span>
                </label>
                <input
                  type='text'
                  maxLength={32}
                  placeholder='ex.: 1234'
                  value={movimentaData.chamado_ref || ''}
                  onChange={handleCampoTexto('chamado_ref')}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200"
                />
              </div>
              <div className='space-y-2' data-tour-id='movimentar:motivo'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Motivo (interno) <span className='font-normal text-gray-400'>— opcional</span>
                </label>
                <input
                  type='text'
                  maxLength={255}
                  placeholder='ex.: instalação na secretaria de saúde'
                  value={movimentaData.motivo || ''}
                  onChange={handleCampoTexto('motivo')}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200"
                />
              </div>
            </div>
            <p className='-mt-3 text-xs text-gray-500 dark:text-gray-400'>
              O nº do chamado aparece para o cliente no relatório de faturamento. O motivo fica só no histórico interno.
            </p>

            {/* Selected Patrimonios */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4' data-tour-id='movimentar:lista'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-3'>
                Patrimônios Selecionados
              </h3>
              {patrimonios.length > 0 ? (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto'>
                  {patrimonios.map((pat, index) => (
                    <div key={index} className='bg-white dark:bg-gray-600 rounded-lg p-3 border border-gray-200 dark:border-gray-500'>
                      <div className='text-sm'>
                        <div className='font-medium text-gray-900 dark:text-white'>
                          ID: {pat.id}
                        </div>
                        <div className='text-gray-600 dark:text-gray-300'>
                          {pat.equipamento || pat.produto || 'N/A'}
                        </div>
                        <div className='text-xs text-gray-500 dark:text-gray-400'>
                          {pat.nEtiqueta || pat.netiqueta || 'N/A'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
                  Nenhum patrimônio selecionado
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
          <button 
            onClick={handleCloseModal}
            className='px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200'
          >
            Cancelar
          </button>
          <button 
            onClick={handleMovimenta}
            data-tour-id='movimentar:confirmar'
            disabled={!movimentaData.id_cliente || !movimentaData.id_anexo || patrimonios.length === 0}
            className='px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
          >
            Confirmar Movimentação
          </button>
        </div>
      </div>
    </div>
  );
};

export default Movimenta;
